// The ERA matcher's interaction model.
//
// Two selection modes live here, deliberately kept apart:
//
//   ARM + APPLY   one ERA line is armed, carrying a budget (what the scheme
//                 paid). Invoice lines are targets it is applied to. This is
//                 the money path.
//   DISMISS       a multi-select over outstanding ERA lines, taking them off
//                 the to-do list without paying them.
//
// They are separate maps, not one. Arming clears a live dismiss selection and
// vice versa, so only one mode is ever live and the footer is never ambiguous
// about what Confirm will do. Folding them together would mean every reducer
// branch had to ask which mode it was in.
//
// The reducer holds the invariants the server also enforces, so the UI refuses
// before the round trip rather than showing a 422: applied never exceeds the
// budget, a target never takes more than its line owes, and confirm is blocked
// while any shortfall is undecided.

import { useMemo, useReducer } from "react";
import { lineOutstanding } from "../Accounts/lineAllocation";

const r2 = (n) => Number(Number(n || 0).toFixed(2));

export const targetKey = (invoiceId, lineNumber) => `${invoiceId}_${lineNumber}`;
export const eraLineId = (claim, line) =>
  `${claim.eraLogId}:${claim.schemeClaimId}:${line.key}`;

export const PHASE = Object.freeze({
  IDLE: "idle",
  ARMED: "armed",
  SHORTFALLS: "shortfalls",
  CONFIRMING: "confirming",
  SUBMITTING: "submitting",
  DONE: "done",
});

export const SHORTFALL_ACTIONS = Object.freeze({
  BILL_PATIENT: "billPatient",
  DISCOUNT: "discount",
  LEAVE_OUTSTANDING: "leaveOutstanding",
});

export const SHORTFALL_LABELS = Object.freeze({
  [SHORTFALL_ACTIONS.BILL_PATIENT]: "Bill the patient",
  [SHORTFALL_ACTIONS.DISCOUNT]: "Discount",
  [SHORTFALL_ACTIONS.LEAVE_OUTSTANDING]: "Leave outstanding",
});

const initialState = {
  workspace: null,
  loading: true,
  loadError: null,

  armed: null, // { eraLogId, schemeClaimId, eraLineKey, tariffCode, budget }
  targets: {}, // key -> { invoiceId, lineNumber, appliedAmount, maxAmount, suggested, shortfallAction, surplusAction, ... }
  suggestions: {}, // targetKey -> true, for the armed line only

  dismissSelection: {}, // eraLineId -> { eraLogId, schemeClaimId, eraLineKey, paidAmount }
  showDismissed: false,
  showMatched: false,
  showPaidInvoices: false,

  phase: PHASE.IDLE,
  mobileStep: 1,
  error: null,
  idempotencyKey: null,
  result: null,

  // Confirmed pairs waiting for ONE receipt. Confirming a line stages it here
  // (nothing is written); Create receipt posts the whole batch as a single
  // request, so the receipt covers every staged line of one ERA. batchKey is
  // the idempotency key for that receipt, minted when the first line stages.
  batch: [],
  batchKey: null,
};

/** Cheap unique key. Crypto where available, timestamp where not. */
function makeIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Suggestions for the armed line only, keyed for O(1) lookup while rendering. */
function suggestionsFor(workspace, armed) {
  if (!workspace || !armed) return {};
  const out = {};
  for (const s of workspace.suggestions || []) {
    if (
      String(s.eraLogId) === String(armed.eraLogId) &&
      String(s.schemeClaimId) === String(armed.schemeClaimId) &&
      String(s.eraLineKey) === String(armed.eraLineKey)
    ) {
      out[targetKey(s.invoiceId, s.lineNumber)] = true;
    }
  }
  return out;
}

/**
 * Pre-apply the armed line's suggested invoice lines, full amounts, in pane
 * order, until the budget runs out.
 *
 * This is what makes the happy flow two clicks: the scheme usually paid
 * exactly what was billed and the tariff pairing is usually right, so arming
 * should land the user on a filled-in right pane with only Confirm left to
 * press. Every safety stays in force: the amount stays editable, a wrong
 * pairing is one click to replace, a shortfall still demands a decision, and
 * nothing is written until Confirm.
 */
/** The armed line's full workspace record (armed itself only carries keys). */
function findArmedLine(workspace, armed) {
  if (!workspace || !armed) return null;
  const claim = (workspace.eraClaims || []).find(
    (c) =>
      String(c.eraLogId) === String(armed.eraLogId) &&
      String(c.schemeClaimId) === String(armed.schemeClaimId)
  );
  return claim?.lines?.find((l) => String(l.key) === String(armed.eraLineKey)) || null;
}

function autoTargetsFor(workspace, armed, suggestions) {
  if (!workspace || !armed) return {};
  // ONE ERA line pays ONE invoice line, so auto-apply takes the FIRST
  // suggestion in the server's own order and stops. Deliberately NOT pane
  // order: walking the invoices used to land the applied cell on whichever
  // suggested line rendered first, not on the armed line's own suggestion.
  // A different candidate is one click away: selecting it replaces this one.
  for (const s of workspace.suggestions || []) {
    if (
      String(s.eraLogId) !== String(armed.eraLogId) ||
      String(s.schemeClaimId) !== String(armed.schemeClaimId) ||
      String(s.eraLineKey) !== String(armed.eraLineKey)
    ) {
      continue;
    }
    const key = targetKey(s.invoiceId, s.lineNumber);
    if (!suggestions[key]) continue;
    const invoice = (workspace.invoices || []).find(
      (i) => String(i._id) === String(s.invoiceId)
    );
    if (!invoice?.canPayPerLine) continue;
    const line = (invoice.lines || []).find(
      (l) => Number(l.lineNumber) === Number(s.lineNumber)
    );
    if (!line) continue;
    const maxAmount = lineOutstanding(
      { medAidAmount: line.medAidAmount, medAidPaid: line.medAidPaid },
      "medicalAid"
    );
    if (maxAmount <= 0) continue;
    const applied = r2(Math.min(maxAmount, armed.budget));
    return {
      [key]: {
        invoiceId: invoice._id,
        invoiceNumber: invoice.invoiceNumber,
        lineNumber: line.lineNumber,
        tariffCode: line.tariffCode,
        description: line.description,
        lineDate: line.lineDate || null,
        appliedAmount: applied,
        maxAmount,
        suggested: true,
        shortfallAction: null,
        surplusAction: null,
      },
    };
  }
  return {};
}

function reducer(state, action) {
  switch (action.type) {
    case "LOAD_START":
      return { ...state, loading: true, loadError: null };

    case "LOAD_OK": {
      const workspace = action.workspace;
      // A pre-arm from the ERA detail view lands the user straight on the line
      // they clicked, rather than making them find it again.
      let armed = null;
      if (workspace.preArm) {
        const claim = (workspace.eraClaims || []).find(
          (c) =>
            String(c.eraLogId) === String(workspace.preArm.eraLogId) &&
            String(c.schemeClaimId) === String(workspace.preArm.schemeClaimId)
        );
        const line = claim?.lines?.find(
          (l) => String(l.key) === String(workspace.preArm.eraLineKey)
        );
        if (claim && line && line.status === "unmatched" && line.paidAmount >= 0) {
          armed = {
            eraLogId: claim.eraLogId,
            schemeClaimId: claim.schemeClaimId,
            eraLineKey: line.key,
            tariffCode: line.tariffCode,
            patientName: claim.patientName,
            budget: r2(line.paidAmount),
          };
        }
      }
      const preArmSuggestions = suggestionsFor(workspace, armed);
      const preArmTargets = autoTargetsFor(workspace, armed, preArmSuggestions);
      return {
        ...state,
        workspace,
        loading: false,
        loadError: null,
        armed,
        targets: preArmTargets,
        suggestions: preArmSuggestions,
        phase: armed ? PHASE.ARMED : PHASE.IDLE,
        mobileStep: armed ? 2 : 1,
        idempotencyKey: armed ? makeIdempotencyKey() : null,
        error: null,
        batch: [],
        batchKey: null,
      };
    }

    case "LOAD_FAIL":
      return { ...state, loading: false, loadError: action.message };

    case "ARM": {
      const { claim, line } = action;
      // One receipt covers ONE ERA. While lines are staged, only their ERA's
      // lines may be armed; anything else waits for the receipt.
      if (
        state.batch.length > 0 &&
        String(claim.eraLogId) !== String(state.batch[0].eraLogId)
      ) {
        return {
          ...state,
          error:
            "One receipt covers one ERA. Create the receipt for the current ERA before matching another.",
        };
      }
      // A staged line is spoken for until the receipt exists or it is removed.
      if (
        state.batch.some(
          (b) =>
            String(b.schemeClaimId) === String(claim.schemeClaimId) &&
            String(b.eraLineKey) === String(line.key) &&
            String(b.eraLogId) === String(claim.eraLogId)
        )
      ) {
        return state;
      }
      // Re-arming the same line disarms, so a mis-click is one click to undo.
      const same =
        state.armed &&
        String(state.armed.eraLogId) === String(claim.eraLogId) &&
        String(state.armed.schemeClaimId) === String(claim.schemeClaimId) &&
        String(state.armed.eraLineKey) === String(line.key);
      if (same) return reducer(state, { type: "DISARM" });

      const armed = {
        eraLogId: claim.eraLogId,
        schemeClaimId: claim.schemeClaimId,
        eraLineKey: line.key,
        tariffCode: line.tariffCode,
        patientName: claim.patientName,
        budget: r2(line.paidAmount),
      };
      const armSuggestions = suggestionsFor(state.workspace, armed);
      const armTargets = autoTargetsFor(state.workspace, armed, armSuggestions);
      return {
        ...state,
        armed,
        targets: armTargets,
        suggestions: armSuggestions,
        // Arming and dismissing are mutually exclusive modes.
        dismissSelection: {},
        phase: PHASE.ARMED,
        mobileStep: 2,
        idempotencyKey: makeIdempotencyKey(),
        error: null,
      };
    }

    case "DISARM":
      return {
        ...state,
        armed: null,
        targets: {},
        suggestions: {},
        phase: PHASE.IDLE,
        mobileStep: 1,
        idempotencyKey: null,
        error: null,
      };

    case "TOGGLE_TARGET": {
      if (!state.armed) return state;
      const { invoice, line } = action;
      const key = targetKey(invoice._id, line.lineNumber);
      if (
        state.batch.some(
          (b) => targetKey(b.invoiceId, b.lineNumber) === key
        )
      ) {
        return state;
      }

      // Clicking the selected line deselects it.
      if (state.targets[key]) {
        return { ...state, targets: {}, error: null };
      }

      const maxAmount = lineOutstanding(
        { medAidAmount: line.medAidAmount, medAidPaid: line.medAidPaid },
        "medicalAid"
      );
      // A settled line is a dead target for money, but a zero-budget ("Not
      // paid") ERA line may still map onto it as an acknowledgment: the
      // patient already carries it and nothing needs to move.
      if (maxAmount <= 0 && state.armed.budget > 0) return state;

      // ONE ERA line pays ONE invoice line. Selecting a line REPLACES any
      // previous selection rather than adding to it, and always gets the full
      // budget, capped at what the line owes. The server refuses anything
      // else (single_invoice_line_required), so the reducer never builds it.
      const applied = r2(Math.min(maxAmount, state.armed.budget));

      return {
        ...state,
        targets: {
          [key]: {
            invoiceId: invoice._id,
            invoiceNumber: invoice.invoiceNumber,
            lineNumber: line.lineNumber,
            tariffCode: line.tariffCode,
            description: line.description,
            lineDate: line.lineDate || null,
            appliedAmount: applied,
            maxAmount,
            suggested: !!state.suggestions[key],
            shortfallAction: null,
            surplusAction: null,
          },
        },
        error: null,
      };
    }

    case "STAGE_LINE": {
      // The screen gates this on canConfirm, but re-derive the essentials so
      // a stray dispatch can never stage an undecided or empty pair.
      const entry = Object.entries(state.targets)[0];
      if (!state.armed || !entry) return state;
      const [, target] = entry;
      // R0.00 is a real application when the armed line's budget is R0.00 (a
      // "Not paid" line being mapped); otherwise a zero is a stray dispatch.
      if (!(target.appliedAmount > 0) && state.armed.budget > 0) return state;
      const pairSurplus = r2(state.armed.budget - target.appliedAmount);
      if (pairSurplus > 0.005 && !target.surplusAction) return state;
      if (
        r2(target.maxAmount - target.appliedAmount) > 0.005 &&
        !target.shortfallAction
      ) {
        return state;
      }
      const armedLine = findArmedLine(state.workspace, state.armed);
      return {
        ...state,
        batch: [
          ...state.batch,
          {
            eraLogId: state.armed.eraLogId,
            schemeClaimId: state.armed.schemeClaimId,
            eraLineKey: state.armed.eraLineKey,
            tariffCode: state.armed.tariffCode,
            patientName: state.armed.patientName,
            dateOfService: armedLine?.dateOfService || null,
            paidAmount: state.armed.budget,
            invoiceId: target.invoiceId,
            invoiceNumber: target.invoiceNumber,
            lineNumber: target.lineNumber,
            invoiceTariffCode: target.tariffCode,
            lineDate: target.lineDate || null,
            appliedAmount: target.appliedAmount,
            maxAmount: target.maxAmount,
            shortfallAction: target.shortfallAction || null,
            discountNote: target.discountNote || null,
            surplus: pairSurplus > 0.005 ? pairSurplus : 0,
            surplusAction: pairSurplus > 0.005 ? target.surplusAction : null,
          },
        ],
        batchKey: state.batchKey || makeIdempotencyKey(),
        armed: null,
        targets: {},
        suggestions: {},
        phase: PHASE.IDLE,
        mobileStep: 1,
        idempotencyKey: null,
        error: null,
      };
    }

    case "UNSTAGE_LINE": {
      const batch = state.batch.filter((_, i) => i !== action.index);
      return {
        ...state,
        batch,
        batchKey: batch.length > 0 ? state.batchKey : null,
      };
    }

    case "SET_SURPLUS_ACTION": {
      const entry = Object.entries(state.targets)[0];
      if (!entry) return state;
      const [key, target] = entry;
      return {
        ...state,
        targets: { [key]: { ...target, surplusAction: action.action } },
        error: null,
      };
    }

    case "SET_SHORTFALL_ACTION": {
      const target = state.targets[action.key];
      if (!target) return state;
      return {
        ...state,
        targets: {
          ...state.targets,
          [action.key]: {
            ...target,
            shortfallAction: action.action,
            // The same dispatch carries the discount note, so typing in the note
            // field does not clear the choice that revealed it.
            discountNote:
              action.note !== undefined ? action.note : target.discountNote || "",
          },
        },
        error: null,
      };
    }

    case "TOGGLE_DISMISS_SELECT": {
      const { claim, line } = action;
      const id = eraLineId(claim, line);
      if (state.dismissSelection[id]) {
        const { [id]: _drop, ...rest } = state.dismissSelection;
        return { ...state, dismissSelection: rest };
      }
      return {
        ...state,
        // Dismissing and arming are mutually exclusive modes.
        armed: null,
        targets: {},
        suggestions: {},
        phase: PHASE.IDLE,
        dismissSelection: {
          ...state.dismissSelection,
          [id]: {
            eraLogId: claim.eraLogId,
            schemeClaimId: claim.schemeClaimId,
            eraLineKey: line.key,
            paidAmount: line.paidAmount,
            tariffCode: line.tariffCode,
            patientName: claim.patientName,
          },
        },
      };
    }

    case "SELECT_DISMISS_LINES":
      // Whole-ERA dismiss: replace the selection outright. Same mode rules
      // as TOGGLE_DISMISS_SELECT: dismissing and arming are mutually
      // exclusive.
      return {
        ...state,
        armed: null,
        targets: {},
        suggestions: {},
        phase: PHASE.IDLE,
        dismissSelection: action.selection,
      };

    case "CLEAR_DISMISS_SELECT":
      return { ...state, dismissSelection: {} };

    case "TOGGLE_SHOW_DISMISSED":
      return { ...state, showDismissed: !state.showDismissed };

    case "TOGGLE_SHOW_MATCHED":
      return { ...state, showMatched: !state.showMatched };

    case "TOGGLE_SHOW_PAID_INVOICES":
      return { ...state, showPaidInvoices: !state.showPaidInvoices };

    case "SET_MOBILE_STEP":
      return { ...state, mobileStep: action.step };

    case "SUBMIT_START":
      return { ...state, phase: PHASE.SUBMITTING, error: null };

    case "SUBMIT_OK":
      return { ...state, phase: PHASE.DONE, result: action.result, error: null };

    case "SUBMIT_FAIL":
      return { ...state, phase: PHASE.ARMED, error: action.message };

    case "SET_ERROR":
      return { ...state, error: action.message };

    case "RESET":
      return {
        ...state,
        armed: null,
        targets: {},
        suggestions: {},
        dismissSelection: {},
        phase: PHASE.IDLE,
        mobileStep: 1,
        idempotencyKey: null,
        error: null,
        result: null,
        batch: [],
        batchKey: null,
      };

    default:
      return state;
  }
}

export function useEraMatchModel() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const derived = useMemo(() => {
    const targets = Object.entries(state.targets);
    const applied = r2(targets.reduce((s, [, t]) => s + (t.appliedAmount || 0), 0));
    const budget = state.armed?.budget || 0;
    const remaining = r2(budget - applied);

    // Every short-paid line needs a decision before anything can be confirmed.
    // A zero application counts when the armed line's budget IS zero: mapping
    // a "Not paid" line leaves the target's whole outstanding short, and that
    // remainder is exactly the decision this prompt exists for.
    const shortfalls = targets
      .map(([key, t]) => ({ key, ...t, shortfall: r2(t.maxAmount - t.appliedAmount) }))
      .filter((t) => (t.appliedAmount > 0 || budget === 0) && t.shortfall > 0.005);
    const undecided = shortfalls.filter((t) => !t.shortfallAction);

    // The scheme paid more than the matched line owes. The remainder needs a
    // decision (unallocated funds or ignore) before Confirm unlocks, the same
    // contract as a shortfall in the other direction.
    const soleTarget = targets.length > 0 ? targets[0][1] : null;
    const surplusRaw = soleTarget ? r2(budget - applied) : 0;
    const surplus = surplusRaw > 0.005 ? surplusRaw : 0;
    const surplusAction = surplus > 0 ? soleTarget?.surplusAction || null : null;

    // Staged pairs: lookups for the panes and the running receipt total.
    const stagedEraIds = new Set(
      state.batch.map((b) => `${b.eraLogId}:${b.schemeClaimId}:${b.eraLineKey}`)
    );
    const stagedTargetKeys = new Map(
      state.batch.map((b) => [targetKey(b.invoiceId, b.lineNumber), b.appliedAmount])
    );
    const batchApplied = r2(state.batch.reduce((s, b) => s + (b.appliedAmount || 0), 0));
    const batchBanked = r2(
      batchApplied +
        state.batch.reduce(
          (s, b) => s + (b.surplusAction === "unallocated" ? b.surplus || 0 : 0),
          0
        )
    );

    const dismissList = Object.values(state.dismissSelection);
    const dismissTotal = r2(dismissList.reduce((s, d) => s + (d.paidAmount || 0), 0));

    let outstandingLines = 0;
    let dismissedLines = 0;
    let matchedLines = 0;
    for (const claim of state.workspace?.eraClaims || []) {
      for (const line of claim.lines || []) {
        // The dismissed and matched counts drive the "Show dismissed (N)" /
        // "Show matched (N)" toggles, so they count every such line, zero
        // and reversal lines included.
        // Outstanding stays a money count: only paid lines are work.
        if (line.status === "dismissed") dismissedLines += 1;
        if (line.status === "matched") matchedLines += 1;
        if (line.status === "unmatched" && line.paidAmount > 0) outstandingLines += 1;
      }
    }

    return {
      applied,
      budget,
      remaining,
      targetCount: targets.length,
      shortfalls,
      undecided,
      surplus,
      surplusAction,
      stagedEraIds,
      stagedTargetKeys,
      batchCount: state.batch.length,
      batchApplied,
      batchBanked,
      canCreateReceipt: state.batch.length > 0 && state.phase !== PHASE.SUBMITTING,
      canConfirm:
        !!state.armed &&
        (applied > 0 || (budget === 0 && targets.length > 0)) &&
        undecided.length === 0 &&
        (surplus <= 0 || !!surplusAction) &&
        state.phase !== PHASE.SUBMITTING,
      dismissList,
      dismissCount: dismissList.length,
      dismissTotal,
      outstandingLines,
      dismissedLines,
      matchedLines,
    };
  }, [state]);

  return { state, dispatch, derived };
}

export default useEraMatchModel;
